import { Slider } from "@base-ui/react/slider";
import clsx from "clsx";
import styles from "./RangeSlider.module.css";

export interface RangeSliderProps {
  /** Visible label ("Pack size"). */
  label: string;
  value: readonly [number, number];
  onValueChange: (value: [number, number]) => void;
  min: number;
  max: number;
  step?: number;
  /** Text for one value: the range shown next to the label, and each thumb's spoken value. */
  formatValue: (value: number) => string;
  /** What each thumb sets, for assistive technology. */
  thumbLabels?: readonly [string, string];
  className?: string;
}

/**
 * A range picked with two thumbs (Base UI Slider), the chosen range shown next to the label. For
 * uneven stops (pack sizes), slide over their indices and format each index as its stop.
 */
export function RangeSlider({
  label,
  value,
  onValueChange,
  min,
  max,
  step = 1,
  formatValue,
  thumbLabels = ["Minimum", "Maximum"],
  className,
}: RangeSliderProps) {
  return (
    <Slider.Root
      value={[value[0], value[1]]}
      onValueChange={(next) => {
        if (Array.isArray(next) && next.length === 2) onValueChange([next[0]!, next[1]!]);
      }}
      min={min}
      max={max}
      step={step}
      className={clsx(styles.root, className)}
    >
      <div className={styles.header}>
        <Slider.Label className={styles.label}>{label}</Slider.Label>
        <span className={styles.value} aria-hidden="true">
          {value[0] === value[1] ? formatValue(value[0]) : `${formatValue(value[0])} – ${formatValue(value[1])}`}
        </span>
      </div>
      <Slider.Control className={styles.control}>
        <Slider.Track className={styles.track}>
          <Slider.Indicator className={styles.indicator} />
          {thumbLabels.map((thumbLabel, index) => (
            <Slider.Thumb
              key={thumbLabel}
              index={index}
              className={styles.thumb}
              getAriaLabel={() => thumbLabel}
              getAriaValueText={(_formatted, thumbValue) => formatValue(thumbValue)}
            />
          ))}
        </Slider.Track>
      </Slider.Control>
    </Slider.Root>
  );
}
